// PRD 05 §3.1 — left tree panel. Read-only tree of areas → sub-areas → items.
// Tree mutation lives in PRD 04 (PRO Item Design); this panel only reads.
//
// S2 adds: disease-filter dimming + per-area / per-sub count badges. The
// counts reflect questions that match the current filter, not raw
// `children.length`. Items with zero matches dim to 35% opacity.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, FileText, Info, Loader2 } from 'lucide-react';

import type { ProTreeNode } from '@/features/pro-tree/types/pro-tree.types';

import { useAllQuestionsQuery } from '../hooks/use-item-questions';
import { useDiseaseFilterOptionsQuery } from '../hooks/use-disease-filter-options';
import { useProTreeQuery } from '../hooks/use-questionnaire-tree';
import {
  useQuestionnaireDesignPageStore,
  type DiseaseFilterValue,
} from '../store/questionnaire-design-page.store';
import {
  buildDiseaseLookup,
  itemMatchesDiseaseFilter,
  type DiseaseLookup,
} from '../utils/disease-filter';
import type { Question } from '../types/questionnaire-design.types';
import { QuestionnaireDiseaseFilter } from './QuestionnaireDiseaseFilter';

interface NodeRowProps {
  node: ProTreeNode;
  depth: number;
  // Pre-computed match counts (filter-aware) by node id. -1 means "filter
  // inactive — show the raw child count instead." Items get their own count;
  // areas/subs get a recursive sum.
  matchCountByNodeId: Map<string, number>;
  // SHCT-512 — whether the node is IN the active disease filter, which is not the same question
  // as how many questions it holds. An item in scope that has no questions yet counts 0 and must
  // still be reachable, because writing its first question is the only way that count ever moves.
  includedByNodeId: Map<string, boolean>;
}

function NodeRow({ node, depth, matchCountByNodeId, includedByNodeId }: NodeRowProps) {
  const expanded = useQuestionnaireDesignPageStore(
    (s) => !!s.expandedNodes[node.id],
  );
  const isSelected = useQuestionnaireDesignPageStore(
    (s) => s.selectedTreeNodeId === node.id,
  );
  const filterActive = useQuestionnaireDesignPageStore(
    (s) => s.diseaseFilter !== '',
  );
  const toggleNode = useQuestionnaireDesignPageStore((s) => s.toggleNode);
  const selectItem = useQuestionnaireDesignPageStore((s) => s.selectItem);

  const hasChildren = (node.children?.length ?? 0) > 0;
  const isItem = node.type === 'item';
  const isArea = node.type === 'area';

  const matchCount = matchCountByNodeId.get(node.id) ?? 0;
  // SHCT-512 — dim on EXCLUSION, never on an empty count. Keying this on `matchCount === 0` made
  // a zero-question item indistinguishable from an out-of-scope one, so an item whose Scope
  // matched the filter was greyed out and refused the click — and the only way to give it a
  // question was through the click it refused. [ADM.047] says the count is not the test.
  const isDimmed = filterActive && !(includedByNodeId.get(node.id) ?? true);
  // SHCT-239 (G15, Option 1) — an item that isn't in the active disease code is
  // dimmed AND non-clickable: the user must reselect a matching disease code to
  // return to it. Areas/subs stay toggleable so the tree is still navigable.
  const itemDisabled = isItem && isDimmed;

  const onClick = () => {
    if (isItem) {
      if (itemDisabled) return;
      selectItem(node.id);
    } else {
      toggleNode(node.id);
    }
  };

  return (
    <>
      <div
        role="treeitem"
        aria-expanded={hasChildren ? expanded : undefined}
        aria-selected={isSelected}
        aria-disabled={itemDisabled || undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        tabIndex={0}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          padding: `6px 12px 6px ${depth * 16 + 12}px`,
          cursor: itemDisabled ? 'not-allowed' : 'pointer',
          fontSize: 'var(--font-size-sm)',
          userSelect: 'none',
          transition: 'background 0.15s, opacity 0.15s',
          background: isSelected ? 'var(--badge-primary-bg)' : 'transparent',
          color: isSelected ? 'var(--badge-primary-text)' : undefined,
          fontWeight: isSelected ? 'var(--font-weight-medium)' : undefined,
          borderRight: isSelected ? '3px solid var(--info)' : undefined,
          opacity: isDimmed ? 0.35 : 1,
        }}
        onMouseEnter={(e) => {
          if (!isSelected && !itemDisabled) e.currentTarget.style.background = 'var(--accent)';
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.background = 'transparent';
        }}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown size={14} style={{ color: 'var(--muted-foreground)' }} />
          ) : (
            <ChevronRight size={14} style={{ color: 'var(--muted-foreground)' }} />
          )
        ) : isItem ? (
          <FileText
            size={14}
            style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}
          />
        ) : (
          <span style={{ width: 14, flexShrink: 0 }} />
        )}

        <span
          style={{
            color: 'var(--info)',
            fontSize: 11,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {node.code}
        </span>

        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: isArea ? 600 : undefined,
            color: isArea ? 'var(--foreground)' : undefined,
          }}
        >
          {node.label}
        </span>

        {/* Count badge — filter-aware on areas/subs, raw fall-back when filter is
            off so the panel still hints at branch size. */}
        {node.type === 'sub' && hasChildren ? (
          filterActive && matchCount > 0 ? (
            <span
              style={{
                fontSize: 10,
                padding: '1px 6px',
                borderRadius: 8,
                background: 'var(--badge-primary-bg)',
                color: 'var(--badge-primary-text)',
                marginLeft: 'auto',
              }}
            >
              {matchCount}
            </span>
          ) : (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--muted-foreground)',
                marginLeft: 'auto',
              }}
            >
              {node.children!.length}
            </span>
          )
        ) : null}
        {node.type === 'area' && filterActive && matchCount > 0 ? (
          <span
            style={{
              fontSize: 10,
              padding: '1px 6px',
              borderRadius: 8,
              background: 'var(--badge-primary-bg)',
              color: 'var(--badge-primary-text)',
              marginLeft: 'auto',
            }}
          >
            {matchCount}
          </span>
        ) : null}
      </div>

      {hasChildren && expanded
        ? node.children!.map((child) => (
            <NodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              matchCountByNodeId={matchCountByNodeId}
              includedByNodeId={includedByNodeId}
            />
          ))
        : null}
    </>
  );
}

function TreeBody({
  tree,
  matchCountByNodeId,
  includedByNodeId,
}: {
  tree: ProTreeNode[];
  matchCountByNodeId: Map<string, number>;
  includedByNodeId: Map<string, boolean>;
}) {
  return (
    <div role="tree" style={{ padding: 'var(--space-2) 0' }}>
      {tree.map((node) => (
        <NodeRow
          key={node.id}
          node={node}
          depth={0}
          matchCountByNodeId={matchCountByNodeId}
          includedByNodeId={includedByNodeId}
        />
      ))}
    </div>
  );
}

function TreeLoading() {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        color: 'var(--muted-foreground)',
        fontSize: 'var(--font-size-xs)',
        gap: 8,
      }}
    >
      <Loader2 size={14} className="animate-spin" /> {t('tree.loading')}
    </div>
  );
}

function TreeError() {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        padding: 24,
        color: 'var(--destructive)',
        fontSize: 'var(--font-size-xs)',
        textAlign: 'center',
      }}
    >
      {t('tree.error')}
    </div>
  );
}

// Count every question whose item sits under `node` (recursively) and that
// matches the active disease filter. Returns 0 when the filter rejects all
// questions in the subtree, even if the subtree has items.
export function buildMatchCountMap(
  tree: ProTreeNode[],
  questionsByItem: Map<string, Question[]>,
  filter: DiseaseFilterValue,
  lookup: DiseaseLookup,
): { counts: Map<string, number>; included: Map<string, boolean> } {
  const out = new Map<string, number>();
  // SHCT-512 — kept SEPARATE from the counts on purpose. The badge answers "how many questions
  // are in scope here"; this answers "is this row in scope at all". They only agree for an item
  // that already has questions, and it was the disagreement — in scope, zero questions — that
  // the tree got wrong.
  const included = new Map<string, boolean>();
  const walk = (node: ProTreeNode): number => {
    if (node.type === 'item') {
      // [ADM.046] — 매칭 기준은 항목의 Scope다. [ADM.047] — 판정에 **보유 문항 수를 쓰지 않는다**:
      // Scope가 해당하면 문항 0개여도 포함(첫 문항 작성 경로 확보), 해당하지 않으면 문항을 아무리
      // 많이 가져도 제외한다. 이 판정을 `included` 맵으로 따로 내보낸다 — 뱃지 숫자로 흐리기를
      // 결정하면 「범위 안 · 문항 0개」가 「범위 밖」과 구분되지 않는다 [SHCT-512]. 뱃지 숫자는 「포함 항목들의 문항 수 합계」이므로, 포함된 항목은 자기
      // 문항을 전부 세고 제외된 항목은 0이다.
      const isIncluded = itemMatchesDiseaseFilter(node, filter, lookup);
      const matched = isIncluded ? (questionsByItem.get(node.id) ?? []).length : 0;
      out.set(node.id, matched);
      included.set(node.id, isIncluded);
      return matched;
    }
    let total = 0;
    // An area or sub-area is in scope when ANY item beneath it is — dimming a parent whose
    // children are reachable would hide the path to them.
    let anyIncluded = false;
    for (const child of node.children ?? []) {
      total += walk(child);
      if (included.get(child.id)) anyIncluded = true;
    }
    out.set(node.id, total);
    included.set(node.id, anyIncluded);
    return total;
  };
  if (!Array.isArray(tree)) return { counts: out, included };
  for (const root of tree) walk(root);
  return { counts: out, included };
}

export function QuestionnaireTreePanel() {
  const { t } = useTranslation('questionnaire-design');
  const { data: tree, isLoading: treeLoading, isError: treeError } = useProTreeQuery();
  const { data: allQuestions } = useAllQuestionsQuery();
  const { data: diseaseGroups } = useDiseaseFilterOptionsQuery();
  const diseaseFilter = useQuestionnaireDesignPageStore((s) => s.diseaseFilter);

  // Index of questions by item id keeps the recursive count linear in the
  // number of questions instead of N*M (items * questions).
  const questionsByItem = useMemo(() => {
    const map = new Map<string, Question[]>();
    for (const q of allQuestions ?? []) {
      const bucket = map.get(q.itemId);
      if (bucket) bucket.push(q);
      else map.set(q.itemId, [q]);
    }
    return map;
  }, [allQuestions]);

  const lookup = useMemo(() => {
    const groupList = (diseaseGroups ?? []).map((entry) => entry.group);
    const codesByGroup = new Map(
      (diseaseGroups ?? []).map((entry) => [entry.group.id, entry.codes]),
    );
    return buildDiseaseLookup(groupList, codesByGroup);
  }, [diseaseGroups]);

  const { counts: matchCountByNodeId, included: includedByNodeId } = useMemo(
    () => buildMatchCountMap(tree ?? [], questionsByItem, diseaseFilter, lookup),
    [tree, questionsByItem, diseaseFilter, lookup],
  );

  return (
    <aside
      style={{
        width: 280,
        minWidth: 280,
        borderRight: '1px solid var(--border)',
        overflowY: 'auto',
        background: '#fafafa',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <QuestionnaireDiseaseFilter />

      <div
        style={{
          padding: 'var(--space-4)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--muted-foreground)',
          }}
        >
          {t('tree.title')}
        </span>
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            color: 'var(--muted-foreground)',
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 4,
          }}
        >
          <Info size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{t('tree.info')}</span>
        </div>
      </div>

      {treeLoading ? (
        <TreeLoading />
      ) : treeError ? (
        <TreeError />
      ) : (
        <TreeBody
          tree={tree ?? []}
          matchCountByNodeId={matchCountByNodeId}
          includedByNodeId={includedByNodeId}
        />
      )}
    </aside>
  );
}
